<!-- Upload Progress Popup Component -->
<div class="upload-progress-popup" 
     *ngIf="isVisible"
     [class.minimized]="isMinimized"
     [class.dragging]="isDragging"
     [style.right.px]="position.x"
     [style.bottom.px]="position.y">
  
  <!-- Header (Always visible) -->
  <div class="popup-header" (mousedown)="onDragStart($event)">
    <!-- Status Icon with Animation -->
    <div class="header-status">
      <mat-icon *ngIf="currentSession?.status === 'uploading'" class="spinning status-icon uploading">
        cloud_upload
      </mat-icon>
      <mat-icon *ngIf="currentSession?.status === 'completed'" class="status-icon completed">
        check_circle
      </mat-icon>
      <mat-icon *ngIf="currentSession?.status === 'failed'" class="status-icon failed">
        error
      </mat-icon>
    </div>
    
    <!-- Title and Summary -->
    <div class="header-content">
      <div class="header-title">
        <span class="title-text">File Upload</span>
        <span class="file-count">{{ uploadFiles.length }} file{{ uploadFiles.length !== 1 ? 's' : '' }}</span>
      </div>
      <div class="header-status-text" *ngIf="isMinimized">
        {{ getSessionStatusText() }}
      </div>
    </div>
    
    <!-- Overall Progress (visible when minimized) -->
    <div class="header-progress" *ngIf="isMinimized && currentSession?.status === 'uploading'">
      <div class="mini-progress-bar">
        <div class="mini-progress-fill" [style.width.%]="overallProgress"></div>
      </div>
      <span class="progress-text">{{ overallProgress | number:'1.0-0' }}%</span>
    </div>
    
    <!-- Header Actions -->
    <div class="header-actions">
      <button mat-icon-button 
              class="minimize-btn"
              (click)="toggleMinimize(); $event.stopPropagation()"
              [matTooltip]="isMinimized ? 'Expand' : 'Minimize'">
        <mat-icon>{{ isMinimized ? 'expand_less' : 'expand_more' }}</mat-icon>
      </button>
      <button mat-icon-button 
              class="close-btn"
              (click)="closePopup(); $event.stopPropagation()"
              matTooltip="Close">
        <mat-icon>close</mat-icon>
      </button>
    </div>
  </div>

  <!-- Expanded Content -->
  <div class="popup-content" *ngIf="!isMinimized">
    
    <!-- Overall Progress Section -->
    <div class="overall-progress-section">
      <div class="progress-header">
        <span class="progress-label">Overall Progress</span>
        <span class="progress-percentage">{{ overallProgress | number:'1.0-0' }}%</span>
      </div>
      <mat-progress-bar 
        [mode]="currentSession?.status === 'uploading' ? 'determinate' : 'determinate'"
        [value]="overallProgress"
        [class.completed]="currentSession?.status === 'completed'"
        [class.failed]="getFailedCount() > 0">
      </mat-progress-bar>
      <div class="progress-summary">
        <span *ngIf="getCompletedCount() > 0" class="summary-item completed">
          <mat-icon>check_circle</mat-icon>
          {{ getCompletedCount() }} completed
        </span>
        <span *ngIf="getUploadingCount() > 0" class="summary-item uploading">
          <mat-icon>cloud_upload</mat-icon>
          {{ getUploadingCount() }} uploading
        </span>
        <span *ngIf="getPendingCount() > 0" class="summary-item pending">
          <mat-icon>schedule</mat-icon>
          {{ getPendingCount() }} pending
        </span>
        <span *ngIf="getFailedCount() > 0" class="summary-item failed">
          <mat-icon>error</mat-icon>
          {{ getFailedCount() }} failed
        </span>
      </div>
    </div>

    <!-- File List -->
    <div class="file-list-container">
      <div class="file-list">
        <div *ngFor="let file of uploadFiles; trackBy: trackByFileId" 
             class="file-item"
             [class]="getStatusClass(file.status)">
          
          <!-- File Icon -->
          <div class="file-icon-wrapper">
            <mat-icon class="file-type-icon">{{ getFileIcon(file.name) }}</mat-icon>
            <mat-icon class="file-status-icon" [class]="getStatusClass(file.status)">
              {{ getStatusIcon(file.status) }}
            </mat-icon>
          </div>
          
          <!-- File Info -->
          <div class="file-info">
            <div class="file-name" [matTooltip]="file.name">{{ file.name }}</div>
            <div class="file-meta">
              <span class="file-size">{{ formatFileSize(file.size) }}</span>
              <span *ngIf="file.status === 'uploading'" class="file-uploaded">
                ({{ getUploadedSize(file) }} uploaded)
              </span>
              <span *ngIf="file.error" class="file-error">{{ file.error }}</span>
            </div>
            
            <!-- Individual Progress Bar -->
            <div class="file-progress" *ngIf="file.status === 'uploading' || file.status === 'pending'">
              <mat-progress-bar 
                [mode]="file.status === 'uploading' ? 'determinate' : 'buffer'"
                [value]="file.progress"
                [bufferValue]="file.progress + 10">
              </mat-progress-bar>
              <span class="file-progress-text">{{ file.progress | number:'1.0-0' }}%</span>
            </div>
          </div>
          
          <!-- File Actions -->
          <div class="file-actions">
            <button *ngIf="file.status === 'uploading'"
                    mat-icon-button 
                    class="action-btn cancel-btn"
                    (click)="cancelUpload(file)"
                    matTooltip="Cancel upload">
              <mat-icon>close</mat-icon>
            </button>
            <button *ngIf="file.status === 'failed'"
                    mat-icon-button 
                    class="action-btn retry-btn"
                    (click)="retryUpload(file)"
                    matTooltip="Retry upload">
              <mat-icon>refresh</mat-icon>
            </button>
            <mat-icon *ngIf="file.status === 'completed'" class="success-icon">check_circle</mat-icon>
          </div>
        </div>
      </div>
    </div>

    <!-- Footer Actions -->
    <div class="popup-footer">
      <!-- Cancel All button - shown when uploading -->
      <button mat-raised-button 
              class="cancel-all-btn"
              color="warn"
              (click)="cancelAllUploads()"
              *ngIf="currentSession?.status === 'uploading' || getPendingCount() > 0">
        <mat-icon>cancel</mat-icon>
        Cancel All
      </button>
      <button mat-button 
              class="clear-btn"
              (click)="clearCompleted()"
              *ngIf="getCompletedCount() > 0">
        <mat-icon>clear_all</mat-icon>
        Clear Completed
      </button>
      <button mat-button 
              class="retry-all-btn"
              (click)="retryAllFailed()"
              *ngIf="getFailedCount() > 0">
        <mat-icon>refresh</mat-icon>
        Retry All Failed
      </button>
    </div>
  </div>
</div>
